import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { budgetService } from "../services/budget.service";

// Upsert (Create or Update) budget line
export const upsertBudgetLine = createAsyncThunk("upsertBudgetLine", async (data, { rejectWithValue }) => {
    try {
        return await budgetService.upsertBudgetLine(data);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

// Get budget
export const getBudget = createAsyncThunk("getBudget", async ({ portfolioId, year }, { rejectWithValue }) => {
    try {
        return await budgetService.getBudget(portfolioId, year);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

// Delete budget line
export const deleteBudgetLine = createAsyncThunk("deleteBudgetLine", async (data, { rejectWithValue }) => {
    try {
        return await budgetService.deleteBudgetLine(data);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

const budgetSlice = createSlice({
    name: "pmo-budget",
    initialState: {
        budgetData: null,
        get: {
            isLoading: false,
            isError: false,
            message: null,
        },
        upsertBudgetLine: {
            isLoading: false,
            isError: false,
            message: null,
        },
        deleteBudgetLine: {
            isLoading: false,
            isError: false,
            message: null,
        },
    },
    reducers: {
        resetStateBudget: (state) => {
            state.get.isError = false;
            state.get.message = null;
            state.upsertBudgetLine.isError = false;
            state.upsertBudgetLine.message = null;
            state.deleteBudgetLine.isError = false;
            state.deleteBudgetLine.message = null;
        },
        addTempBudgetLine: (state, action) => {
            //add before total row
            if (state.budgetData && state.budgetData[action.payload.sectionKey]) {
                const totalRowIndex = state.budgetData[action.payload.sectionKey].rows.findIndex(r => r.item === 'Total');
                if (totalRowIndex > 0) {
                    state.budgetData[action.payload.sectionKey].rows.splice(totalRowIndex, 0, action.payload.row);
                } else {
                    state.budgetData[action.payload.sectionKey].rows.unshift(action.payload.row);
                }
            }
        },
        removeTempBudgetLine: (state, action) => {
            if (state.budgetData && state.budgetData[action.payload.sectionKey]) {
                const index = state.budgetData[action.payload.sectionKey].rows.findIndex(row => row.id === action.payload.id);
                if (index !== -1) {
                    state.budgetData[action.payload.sectionKey].rows.splice(index, 1);
                }
            }
        },
    },
    extraReducers: (builder) => {
        // Get budget
        builder.addCase(getBudget.pending, (state) => {
            state.get.isLoading = true;
        });
        builder.addCase(getBudget.fulfilled, (state, action) => {
            state.get.isLoading = false;
            state.budgetData = action.payload;
            state.get.isError = false;
        });
        builder.addCase(getBudget.rejected, (state, action) => {
            state.get.isLoading = false;
            state.get.isError = true;
            state.get.message = action.payload?.message || "Failed to fetch budget";
        });

        // Upsert budget line (Create or Update)
        builder.addCase(upsertBudgetLine.pending, (state) => {
            state.upsertBudgetLine.isLoading = true;
        });
        builder.addCase(upsertBudgetLine.fulfilled, (state, action) => {
            state.upsertBudgetLine.isLoading = false;
            state.budgetData = action.payload;
            state.upsertBudgetLine.isError = false;
        });
        builder.addCase(upsertBudgetLine.rejected, (state, action) => {
            state.upsertBudgetLine.isLoading = false;
            state.upsertBudgetLine.isError = true;
            state.upsertBudgetLine.message = action.payload?.message || "Failed to upsert budget line";
        });

        // Delete budget line
        builder.addCase(deleteBudgetLine.pending, (state) => {
            state.deleteBudgetLine.isLoading = true;
        });
        builder.addCase(deleteBudgetLine.fulfilled, (state, action) => {
            state.deleteBudgetLine.isLoading = false;
            state.budgetData = action.payload;
            state.deleteBudgetLine.isError = false;
        });
        builder.addCase(deleteBudgetLine.rejected, (state, action) => {
            state.deleteBudgetLine.isLoading = false;
            state.deleteBudgetLine.isError = true;
            state.deleteBudgetLine.message = action.payload?.message || "Failed to delete budget line";
        });
    },
});

export default budgetSlice.reducer;
export const { resetStateBudget, addTempBudgetLine, removeTempBudgetLine } = budgetSlice.actions;

